import React, { useEffect, useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

// One switch per optional feature, so anything clever can be turned off without a deploy.
export const AdminFeatures = () => {
  const { toast } = useToast();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/features").then((r) => setD(r.data)).catch(() => setD(null)); }, []);

  const toggle = async (patch) => {
    setBusy(true);
    const next = { ...d, ...patch };
    try {
      await api.post("/admin/features", { aiDescription: !!next.aiDescription, pinFix: !!next.pinFix, pricingPublic: !!next.pricingPublic });
      setD(next);
      const on = Object.values(patch)[0];
      toast({ title: on ? "Turned on" : "Turned off" });
    } catch { toast({ title: "Could not save that" }); }
    finally { setBusy(false); }
  };

  if (!d) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-features">
      <h2 className="font-bold text-foreground flex items-center gap-2">
        <Sparkles size={18} className="text-[#16a34a]" /> Optional features
      </h2>
      <label className="flex items-start gap-3 mt-3 text-sm text-foreground cursor-pointer">
        <input type="checkbox" checked={!!d.aiDescription} disabled={busy}
          onChange={(e) => toggle({ aiDescription: e.target.checked })} data-testid="feature-ai-description"
          className="h-4 w-4 accent-[#16a34a] mt-0.5" />
        <span>
          <span className="font-semibold flex items-center gap-1.5">
            AI deal descriptions {busy && <Loader2 className="animate-spin" size={13} />}
          </span>
          <span className="block text-xs text-muted-foreground mt-0.5">
            Shops get a “Write it for me” button on the description box. It reads the deal's first
            photo, so drafts cost around US$0.0015 each, capped at 20 per shop per day. Turn it off
            and the button disappears — nothing else changes.
          </span>
          <span className="block text-xs font-semibold text-foreground mt-1" data-testid="feature-ai-usage">
            {d.draftsLast30Days ?? 0} drafts in the last 30 days
            {d.draftsLast30Days ? ` · about US$${(d.draftsLast30Days * 0.0015).toFixed(2)}` : ""}
          </span>
        </span>
      </label>
      <label className="flex items-start gap-3 mt-3 text-sm text-foreground cursor-pointer">
        <input type="checkbox" checked={!!d.pinFix} disabled={busy}
          onChange={(e) => toggle({ pinFix: e.target.checked })} data-testid="feature-pin-fix"
          className="h-4 w-4 accent-[#16a34a] mt-0.5" />
        <span>
          <span className="font-semibold">One-tap pin fix</span>
          <span className="block text-xs text-muted-foreground mt-0.5">
            When a shop's map pin sits 3km or more from the Google listing under its own name, the
            warning email carries a "Move my pin here" button. It opens a confirmation page showing
            both spots and moves the pin — and the same pin on that shop's deals — in one tap. The
            link is single-use and dies after 14 days. Turn it off and the email just points them at
            their locations page instead.
          </span>
        </span>
      </label>
      <label className="flex items-start gap-3 mt-3 text-sm text-foreground cursor-pointer">
        <input type="checkbox" checked={d.pricingPublic !== false} disabled={busy}
          onChange={(e) => toggle({ pricingPublic: e.target.checked })} data-testid="feature-pricing-public"
          className="h-4 w-4 accent-[#16a34a] mt-0.5" />
        <span>
          <span className="font-semibold">Public pricing page</span>
          <span className="block text-xs text-muted-foreground mt-0.5">
            When on, anyone (including logged-out visitors and shoppers) can view the Pricing page
            and it shows in the sidebar for everyone. Upgrade buttons still require a business login.
            Turn it off to revert to the old behaviour — Pricing is reachable by business and admin
            accounts only, and visitors are sent to log in.
          </span>
        </span>
      </label>
    </div>
  );
};

export default AdminFeatures;
